import { useCallback, useEffect, useState } from "react";

import { OrderChip, PaymentChip, RefundChip, ShipmentChip, orderLabel } from "@/components/chips";
import * as admin from "@/lib/api/admin";
import { ApiError } from "@/lib/api-client";
import type {
  AdminOrder,
  ApiPayment,
  OrderStatus,
  PaymentMethod,
  ShippingOptions,
} from "@/lib/api/types";
import { cn, formatDateTime, formatMinor } from "@/lib/utils";

/**
 * Orders — the screen an operator opens the console to look at.
 *
 * ── What the payment column is for ─────────────────────────────────────────
 *
 * An order status and a payment status are different facts and this screen
 * shows both, side by side, because the interesting rows are the ones where
 * they disagree. `CONFIRMED` + `Part refunded` is a real state. So is `Pending`
 * + `Pending` — an online order whose UPI collect is still sitting unapproved,
 * holding stock. Collapsing the two into one column would hide exactly the
 * rows worth looking at.
 *
 * ── Why advancing offers one destination ───────────────────────────────────
 *
 * The API accepts forward moves only, and refuses to advance an unpaid online
 * order at all. Rather than surface a dropdown of five statuses and let four of
 * them 400, the button offers the single next step — and is simply absent when
 * the order cannot move.
 */

type State =
  | { phase: "loading" }
  | { phase: "ready"; orders: AdminOrder[]; total: number; pages: number }
  | { phase: "error"; message: string };

const STATUSES: OrderStatus[] = [
  "PENDING",
  "CONFIRMED",
  "PACKED",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
];

export function OrdersRoute() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<OrderStatus | "">("");
  const [method, setMethod] = useState<PaymentMethod | "">("");
  const [search, setSearch] = useState("");
  const [state, setState] = useState<State>({ phase: "loading" });
  const [open, setOpen] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await admin.fetchOrders(
          {
            page,
            status: status || undefined,
            paymentMethod: method || undefined,
            search: search.trim() || undefined,
          },
          controller.signal,
        );

        if (controller.signal.aborted) return;

        setState({
          phase: "ready",
          orders: result.data,
          total: result.meta.total,
          pages: Math.max(1, result.meta.pages),
        });
      } catch (thrown) {
        if (controller.signal.aborted) return;

        setState({
          phase: "error",
          message: thrown instanceof ApiError ? thrown.message : "Could not reach the API.",
        });
      }
    })();

    return () => controller.abort();
  }, [page, status, method, search, reload]);

  /** Any mutation re-reads the list rather than patching a row in place — the
   *  server may have changed more than the row that was acted on (a cancel also
   *  books a refund), and a stale table is worse than a second fetch. */
  const refresh = useCallback(() => setReload((n) => n + 1), []);

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-[20px] font-semibold text-ink">Orders</h1>
        <p className="mt-1 text-[13px] text-muted">
          {state.phase === "ready"
            ? `${state.total} order${state.total === 1 ? "" : "s"}`
            : state.phase === "loading"
              ? "Loading…"
              : "Could not load orders"}
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Order number…"
          className="w-[200px] rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink placeholder:text-faint"
        />

        <Select
          value={status}
          onChange={(value) => {
            setStatus(value as OrderStatus | "");
            setPage(1);
          }}
          options={[
            { value: "", label: "All statuses" },
            ...STATUSES.map((s) => ({ value: s, label: orderLabel(s) })),
          ]}
        />

        <Select
          value={method}
          onChange={(value) => {
            setMethod(value as PaymentMethod | "");
            setPage(1);
          }}
          options={[
            { value: "", label: "All payments" },
            { value: "ONLINE", label: "Online" },
            // COD is withdrawn and no new order can be one, but the filter
            // stays: the order book still contains the ones placed before it
            // went, and finding them is the only reason to look.
            { value: "COD", label: "Cash on delivery (withdrawn)" },
          ]}
        />
      </div>

      {state.phase === "error" ? (
        <p className="rounded-lg bg-bad-tint px-4 py-3 text-[13px] text-bad">{state.message}</p>
      ) : state.phase === "loading" ? (
        <p className="text-[13.5px] text-muted">Loading orders…</p>
      ) : state.orders.length === 0 ? (
        <p className="rounded-xl border border-line bg-surface px-4 py-8 text-center text-[13.5px] text-muted">
          No orders match those filters.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-line bg-surface">
            <table className="w-full border-collapse text-left text-[13px]">
              <thead>
                <tr className="border-b border-line text-[11px] tracking-wide text-faint uppercase">
                  <th className="px-4 py-3 font-semibold">Order</th>
                  <th className="px-4 py-3 font-semibold">Customer</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Payment</th>
                  <th className="px-4 py-3 text-right font-semibold">Total</th>
                  <th className="px-4 py-3 font-semibold">Placed</th>
                </tr>
              </thead>
              <tbody>
                {state.orders.map((order) => (
                  <OrderRow
                    key={order.orderNumber}
                    order={order}
                    expanded={open === order.orderNumber}
                    onToggle={() =>
                      setOpen((current) =>
                        current === order.orderNumber ? null : order.orderNumber,
                      )
                    }
                    onChanged={refresh}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <Pager page={page} pages={state.pages} onChange={setPage} />
        </>
      )}
    </div>
  );
}

function OrderRow({
  order,
  expanded,
  onToggle,
  onChanged,
}: {
  order: AdminOrder;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className={cn(
          "cursor-pointer border-b border-line last:border-0 hover:bg-canvas",
          expanded && "bg-canvas",
        )}
      >
        <td className="px-4 py-3">
          <span className="block font-medium text-ink">{order.orderNumber}</span>
          <span className="block text-[11.5px] text-faint">
            {order.itemCount} item{order.itemCount === 1 ? "" : "s"}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="block text-body">{order.customer.name ?? "—"}</span>
          <span className="block text-[11.5px] text-faint">{order.customer.email ?? "—"}</span>
        </td>
        <td className="px-4 py-3">
          <OrderChip status={order.status} />
        </td>
        <td className="px-4 py-3">
          {order.paymentMethod === "COD" ? (
            /* A pre-withdrawal order. Nothing was ever collected online for
               it, so there is no payment row to show and never will be. */
            <span className="text-[12px] text-muted">Cash on delivery</span>
          ) : order.payment ? (
            <PaymentChip status={order.payment.status} />
          ) : (
            /* An ONLINE order with no payment row: the gateway order could not
               be created. The sweeper will release it — worth seeing meanwhile. */
            <span className="text-[12px] text-bad">Not started</span>
          )}
        </td>
        <td className="px-4 py-3 text-right font-medium text-ink tabular-nums">
          {formatMinor(order.total.minor)}
        </td>
        <td className="px-4 py-3 text-[12px] text-muted">{formatDateTime(order.placedAt)}</td>
      </tr>

      {expanded ? (
        <tr className="border-b border-line last:border-0">
          <td colSpan={6} className="bg-canvas px-4 py-4">
            <OrderDetail orderNumber={order.orderNumber} onChanged={onChanged} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

/**
 * The expanded row. Fetched separately rather than expanded from the list row,
 * because the detail carries every payment attempt and its event timeline —
 * which would be most of the payload if the list carried it for every row.
 */
function OrderDetail({ orderNumber, onChanged }: { orderNumber: string; onChanged: () => void }) {
  const [order, setOrder] = useState<AdminOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const fresh = await admin.fetchOrder(orderNumber, controller.signal);
        if (!controller.signal.aborted) setOrder(fresh);
      } catch (thrown) {
        if (!controller.signal.aborted) {
          setError(thrown instanceof ApiError ? thrown.message : "Could not load this order.");
        }
      }
    })();

    return () => controller.abort();
  }, [orderNumber]);

  async function act(run: () => Promise<AdminOrder>) {
    setBusy(true);
    setError(null);

    try {
      setOrder(await run());
      onChanged();
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  if (error && !order) return <p className="text-[13px] text-bad">{error}</p>;
  if (!order) return <p className="text-[13px] text-muted">Loading…</p>;

  /**
   * A live shipment takes SHIPPED off the button.
   *
   * Booking an AWB and advancing to SHIPPED are one operator intent; leaving
   * both would let them disagree — an order marked shipped with no parcel
   * behind it, or the reverse. Once a booking exists the courier's own pickup
   * scan is what moves the order, and the button that would race it is gone.
   */
  const live = (order.shipments ?? []).find(
    (shipment) =>
      shipment.status !== "CANCELLED" &&
      shipment.status !== "FAILED" &&
      shipment.status !== "RETURNED",
  );

  const next = admin.nextStatus(order.status, live !== undefined);
  const canCancel = admin.CANCELLABLE.includes(order.status);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <div>
        <Section title="Items">
          <ul className="flex flex-col gap-1.5">
            {order.items.map((item) => (
              <li
                key={`${item.productSlug}-${item.colourName ?? ""}`}
                className="flex gap-3 text-[12.5px]"
              >
                <span className="flex-1 text-body">
                  {item.productName}
                  {item.colourName ? ` · ${item.colourName}` : ""}
                </span>
                <span className="text-muted">×{item.quantity}</span>
                <span className="w-20 text-right font-medium tabular-nums">
                  {formatMinor(item.lineTotal.minor)}
                </span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Timeline">
          <ul className="flex flex-col gap-1.5">
            {(order.events ?? []).map((event, index) => (
              <li key={index} className="flex gap-3 text-[12.5px]">
                <span className="w-[130px] shrink-0 text-faint tabular-nums">
                  {formatDateTime(event.createdAt)}
                </span>
                <span className="flex-1 text-body">{event.note ?? orderLabel(event.status)}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section title="Shipping to">
          <p className="text-[12.5px] leading-relaxed text-body">
            {order.shippingAddress.fullName}
            <br />
            {order.shippingAddress.line1}
            {order.shippingAddress.line2 ? `, ${order.shippingAddress.line2}` : ""}
            <br />
            {order.shippingAddress.city}, {order.shippingAddress.state}{" "}
            {order.shippingAddress.pincode}
            <br />
            {order.shippingAddress.phone}
          </p>

          <ShippingBlock
            order={order}
            onChanged={() => {
              void admin.fetchOrder(order.orderNumber).then(setOrder);
              onChanged();
            }}
          />
        </Section>
      </div>

      <div>
        {(order.payments ?? []).map((payment) => (
          <PaymentBlock key={payment.id} payment={payment} />
        ))}

        {order.refunds.length > 0 ? (
          <Section title="Refunds">
            <ul className="flex flex-col gap-2">
              {order.refunds.map((refund) => (
                <li key={refund.refundId} className="text-[12.5px]">
                  <div className="flex items-center gap-2">
                    <span className="font-medium tabular-nums">
                      {formatMinor(refund.amount.minor)}
                    </span>
                    <RefundChip status={refund.status} />
                    <span className="text-faint">
                      {refund.reason.toLowerCase().replace(/_/g, " ")}
                    </span>
                  </div>
                  {refund.failureReason ? (
                    <p className="mt-0.5 text-[11.5px] text-bad">{refund.failureReason}</p>
                  ) : null}
                  {refund.arn ? (
                    <p className="mt-0.5 text-[11.5px] text-faint">ARN {refund.arn}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {error ? <p className="mb-3 text-[12.5px] text-bad">{error}</p> : null}

        <div className="flex flex-col gap-2">
          {next ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void act(() => admin.advanceOrder(order.orderNumber, next))}
              className="rounded-lg bg-ink px-3 py-2 text-[12.5px] font-semibold text-white disabled:opacity-50"
            >
              Mark {orderLabel(next).toLowerCase()}
            </button>
          ) : null}

          {canCancel ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                const reason = window.prompt("Why is this order being cancelled?");
                if (!reason) return;

                /**
                 * The confirm is about money, not about cancelling. Cancelling
                 * restores stock and is recoverable by re-ordering; a refund
                 * leaves for the customer's bank and does not come back.
                 */
                const paid = order.payment?.status === "PAID" ? order.payment : null;

                const refund =
                  paid === null ||
                  window.confirm(
                    `Refund ${formatMinor(paid.amount.minor - paid.refunded.minor)} to the customer?\n\nCancel this dialog to cancel the order WITHOUT refunding.`,
                  );

                void act(() => admin.cancelOrder(order.orderNumber, reason, refund));
              }}
              className="rounded-lg border border-line px-3 py-2 text-[12.5px] font-semibold text-body hover:border-bad hover:text-bad disabled:opacity-50"
            >
              Cancel this order
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * The parcel: book one, or show the one that exists.
 *
 * **This replaces the "Mark shipped" button**, and the replacement is the
 * point. Booking an AWB and advancing the order are one action taken by one
 * person for one reason, and splitting them across two controls is how an order
 * ends up SHIPPED with nothing booked.
 *
 * Nothing here lets an operator type a weight. The parcel is assembled
 * server-side from the products' own measurements — a form field would be a
 * second source of truth for what is in the box, and the courier weighs the
 * box.
 */
function ShippingBlock({ order, onChanged }: { order: AdminOrder; onChanged: () => void }) {
  const [options, setOptions] = useState<ShippingOptions | null>(null);
  const [courierId, setCourierId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const live = (order.shipments ?? []).find(
    (shipment) =>
      shipment.status !== "CANCELLED" &&
      shipment.status !== "FAILED" &&
      shipment.status !== "RETURNED",
  );

  /**
   * Only from CONFIRMED or PACKED. A PENDING order has not been paid for, and a
   * cancelled one has been given back to stock — booking either is a parcel
   * nobody meant to send.
   */
  const bookable = order.status === "CONFIRMED" || order.status === "PACKED";

  async function run(action: () => Promise<unknown>, failure: string): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      await action();
      onChanged();
    } catch (thrown) {
      setError(thrown instanceof ApiError ? thrown.message : failure);
    } finally {
      setBusy(false);
    }
  }

  if (live) {
    return (
      <div className="mt-3 border-t border-line pt-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <ShipmentChip status={live.status} />
          {live.courierName ? (
            <span className="text-[12.5px] font-medium text-body">{live.courierName}</span>
          ) : null}
        </div>

        {live.failureReason ? (
          <p className="mb-2 rounded bg-bad-tint px-2 py-1 text-[11.5px] leading-relaxed text-bad">
            {live.failureReason}
          </p>
        ) : null}

        <dl className="mb-2 flex flex-col gap-0.5 text-[11.5px]">
          {live.awb ? <Row label="AWB" value={live.awb} /> : null}
          <Row label="Booking ref" value={live.bookingRef} />
          <Row label="Parcel" value={`${live.parcel.weightGrams} g · ${live.mode.toLowerCase()}`} />
          {live.etaAt ? <Row label="Expected" value={formatDateTime(live.etaAt)} /> : null}
        </dl>

        <div className="flex flex-wrap gap-1.5">
          {live.providerShipmentId ? (
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  /**
                   * Blob-ified from base64 rather than linked to. A plain
                   * `<a href download>` at the API carries no Authorization
                   * header, and the envelope wraps `res.json` only — a binary
                   * body would be the one unencrypted response in the system.
                   */
                  const blob = await admin.fetchShipmentLabel(order.orderNumber);
                  const url = URL.createObjectURL(blob);

                  window.open(url, "_blank", "noopener");
                  // Revoked on the next tick: the new tab has already claimed it.
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                }, "Could not fetch the label.")
              }
              className="rounded-lg border border-line px-2.5 py-1 text-[12px] font-semibold text-body hover:bg-canvas hover:text-ink disabled:opacity-50"
            >
              Print label
            </button>
          ) : null}

          {live.providerShipmentId ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(() => admin.syncShipment(live.id), "Could not sync.")}
              className="rounded-lg border border-line px-2.5 py-1 text-[12px] font-semibold text-body hover:bg-canvas hover:text-ink disabled:opacity-50"
            >
              Sync
            </button>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(() => admin.retryShipment(live.id), "Could not retry.")}
              className="rounded-lg border border-line px-2.5 py-1 text-[12px] font-semibold text-body hover:bg-canvas hover:text-ink disabled:opacity-50"
            >
              Retry booking
            </button>
          )}

          {live.status !== "DELIVERED" ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                if (!window.confirm("Cancel this booking with the courier?")) return;
                void run(() => admin.cancelShipment(live.id), "Could not cancel the booking.");
              }}
              className="rounded-lg border border-line px-2.5 py-1 text-[12px] font-semibold text-body hover:border-bad hover:text-bad disabled:opacity-50"
            >
              Cancel booking
            </button>
          ) : null}
        </div>

        {error ? <p className="mt-2 text-[11.5px] text-bad">{error}</p> : null}
      </div>
    );
  }

  if (!bookable) return null;

  return (
    <div className="mt-3 border-t border-line pt-3">
      {options ? (
        <>
          <p className="mb-2 text-[11.5px] text-faint">
            Box {options.parcel.box} · {options.parcel.weightGrams} g · {options.parcel.lengthMm}×
            {options.parcel.breadthMm}×{options.parcel.heightMm} mm
          </p>

          <select
            value={courierId}
            onChange={(event) => setCourierId(event.target.value)}
            className="mb-2 w-full rounded-lg border border-line bg-surface px-2 py-1.5 text-[12.5px] text-ink"
          >
            {/* "Let iCarry choose" is the honest default: an operator who has
                not looked at the list has no opinion, and the aggregator's own
                pick is better than an arbitrary first row. */}
            <option value="">Let the courier choose</option>
            {options.couriers.map((option) => (
              <option key={option.courierId ?? option.courierName} value={option.courierId ?? ""}>
                {option.courierName ?? "Courier"}
                {option.chargeMinor === null ? "" : ` · ${formatMinor(option.chargeMinor)}`}
                {option.days === null ? "" : ` · ${option.days}d`}
              </option>
            ))}
          </select>
        </>
      ) : null}

      <button
        type="button"
        disabled={busy}
        onClick={() =>
          options
            ? void run(
                () => admin.bookShipment(order.orderNumber, { courierId: courierId || undefined }),
                "Could not book the shipment.",
              )
            : void run(
                async () => setOptions(await admin.fetchShippingOptions(order.orderNumber)),
                "Could not fetch courier options.",
              )
        }
        className="w-full rounded-lg bg-ink px-3 py-2 text-[12.5px] font-semibold text-white disabled:opacity-50"
      >
        {busy ? "Working…" : options ? "Book shipment" : "Book shipment…"}
      </button>

      {/* The refusal names what to go and fix — a product with no weight on
          file — because the server refuses for exactly the same reason. */}
      {error ? <p className="mt-2 text-[11.5px] leading-relaxed text-bad">{error}</p> : null}
    </div>
  );
}

function PaymentBlock({ payment }: { payment: ApiPayment }) {
  return (
    <Section title={`Payment · ${payment.gatewayOrderId}`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <PaymentChip status={payment.status} />
        <span className="text-[12.5px] font-medium tabular-nums">
          {formatMinor(payment.amount.minor)}
        </span>
        {payment.method ? (
          <span className="text-[11.5px] text-faint uppercase">{payment.method}</span>
        ) : null}
      </div>

      {/* The gateway's own decline text. This is the reason it is stored: it is
          useless to a shopper and it is the first thing support needs. */}
      {payment.failureReason ? (
        <p className="mb-2 rounded bg-bad-tint px-2 py-1 text-[11.5px] leading-relaxed text-bad">
          {payment.failureReason}
        </p>
      ) : null}

      <dl className="mb-2 flex flex-col gap-0.5 text-[11.5px]">
        {payment.cfPaymentId ? <Row label="Cashfree payment" value={payment.cfPaymentId} /> : null}
        {payment.bankReference ? (
          <Row label="Bank reference" value={payment.bankReference} />
        ) : null}
        {payment.paidAt ? <Row label="Paid" value={formatDateTime(payment.paidAt)} /> : null}
        {payment.refunded.minor > 0 ? (
          <Row label="Refunded" value={formatMinor(payment.refunded.minor)} />
        ) : null}
      </dl>

      {payment.events && payment.events.length > 0 ? (
        <ul className="flex flex-col gap-1 border-t border-line pt-2">
          {payment.events.map((event, index) => (
            <li key={index} className="flex gap-2 text-[11.5px]">
              <span className="w-[120px] shrink-0 text-faint tabular-nums">
                {formatDateTime(event.createdAt)}
              </span>
              <span className="w-[62px] shrink-0 text-faint uppercase">{event.source}</span>
              <span className="flex-1 text-muted">{event.message ?? event.type}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

// ─── Small shared pieces ─────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-4 rounded-lg border border-line bg-surface p-3">
      <h3 className="mb-2 text-[11px] font-bold tracking-wide text-faint uppercase">{title}</h3>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-faint">{label}</dt>
      <dd className="truncate font-medium text-muted">{value}</dd>
    </div>
  );
}

export function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="rounded-lg border border-line bg-surface px-3 py-1.5 text-[13px] text-ink"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

export function Pager({
  page,
  pages,
  onChange,
}: {
  page: number;
  pages: number;
  onChange: (page: number) => void;
}) {
  if (pages <= 1) return null;

  return (
    <div className="mt-4 flex items-center justify-center gap-3">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold disabled:opacity-40"
      >
        Previous
      </button>
      <span className="text-[12.5px] text-muted">
        Page {page} of {pages}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(pages, page + 1))}
        disabled={page === pages}
        className="rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}
